import { inject, Injectable } from "@angular/core";
import
{
    RepositoryDataCollection,
    IFormMetadata,
    IViewMetadata,
    ICombinedViewMetadata,
    IMetadataAction,
    IFormMetadataCustomSaveActions,
    IFormMetadataCustomCancelAction,
    MetadataCollectionVerification,
    AfterSaveContext,
    SaveHandlingType
} from "@tylertech/tyler-api-types";
import { CachedRepositoryService, CacheHelperService, NavigationService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { RepositoryFormData, AugmentedModelObject } from "../interfaces";
import { MetadataFormStack, ArrayHelpers, ObjectHelpers, FormState, MetadataActionHelpers } from "../utilities";
import { MetadataErrorService } from "./metadataErrorService";
import { MetadataActionService } from "./metadataActionService";
import { MetadataViewService } from "./metadataViewService";
import { MetadataEventService } from "./metadataEventService";
import { MetadataInputSelectService } from "./metadataInputSelectService";
import { MetadataLoadViewService } from "./metadataLoadViewService";
import { FormTitleService } from "./formTitleService";
import { MultipleEntityEditService } from "./multipleEntityEditService";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { ProviderStorageService } from "./table/provider.storage.service";
import { MetadataTableService } from "./table/metadataTableService";
import { IntentService } from "@tylertech/intents-module";
import { ResetStateService } from "./reset.state.service";

export enum SaveType
{
    Save = 0,
    SaveAndNext = 1,
    SaveAndNew = 2,
    SaveAndCopy = 3,
}
/**
 * Function that determines if the paired `MetadataFormSaveHandler` should intercept the given `IFormMetadata`.
 * @requires `true` if the fom
 */
export type MetadataFormSavePredicate = (form: IFormMetadata) => boolean;
export interface AfterMetadataFormSave
{
    /**
     * Halts the metadataForm save pipeline.
     *
     * Set to `true` if you wish to handle the post-save behavior of your IFormMetadata.
     * If set to `false` or not set at all, then metadata-form will continue with it's standard navigation pipeline.
     */
    halt?: boolean;
    /**
     * Set to `true` if you wish to bail out of any Add/New or Save/Next functionality.
     */
    cancel?: boolean;
}
/**
 * Function that will handle the save behavior for the `IFormMetadata` and `RepositoryDataCollection` pair given as parameters.
 *
 * @param form `IFormMetadata`
 * @param datacontroller `RepositoryDataCollection`
 *
 * @returns `AfterMetadataFormSave`
 */
export type MetadataFormSaveHandler = (
    form: IFormMetadata,
    datacontroller: RepositoryDataCollection
) => Promise<AfterMetadataFormSave>;
/**
 * Helper service that contains standard actions needed in `metadata-form` components.
 *
 * Not to be confused with the `$formMetadataService` in @tylertech/core-module.
 */
@Injectable({ providedIn: "root" })
export class MetadataFormService extends ResetStateService
{
    initialForm: boolean = false;

    protected readonly saveHandlers: {
        predicate: MetadataFormSavePredicate,
        handler: MetadataFormSaveHandler;
    }[] = [];

    constructor(
        private readonly $intentService: IntentService,
        private readonly $repositoryService: CachedRepositoryService,
        private readonly $cacheHelperService: CacheHelperService,
        private readonly $commonDialogsService: CommonDialogsService,
        private readonly $metadataActionService: MetadataActionService,
        private readonly $metadataErrorService: MetadataErrorService,
        private readonly $metadataViewService: MetadataViewService,
        private $metadataEventService: MetadataEventService,
        private readonly $metadataLoadViewService: MetadataLoadViewService,
        private readonly $metadataTableService: MetadataTableService,
        private readonly $providerStorageService: ProviderStorageService,
        private readonly $metadataInputSelectService: MetadataInputSelectService,
        private $multipleEditEntityService: MultipleEntityEditService,
        private $formTitleService: FormTitleService
    )
    {
        super(inject(NavigationService));
    }

    // TODO - implement methods for standard behaviors; delete's from gridForms.
    // TODO - implement methods for form stack navigation; -return-, cancel?, -next-, -new-, -copy-
    isFormViewOnly(form: IFormMetadata, formData: RepositoryFormData): boolean
    {
        return (
            form.ViewOnly ||
            (!Utilities.IsNullOrUndefinedOrEmpty(form.ViewOnlyProperty) &&
                formData.Entity &&
                formData.Entity[form.ViewOnlyProperty] === true)
        );
    }

    /**
     * Returns `true` if the `IFormMetadata` contains only a single `IGridViewMetadata`; `false` otherwise.
     * @see MetadataViewService.IsGrid
     */
    isGridForm(formMetadata: IFormMetadata): boolean
    {
        return (
            formMetadata.Views.length === 1 &&
            this.$metadataViewService.IsGrid(formMetadata.Views[0]) &&
            Utilities.IsNullOrUndefined(formMetadata.Views[0].SubPropertyName)
        );
    }

    /**
     * Returns `true` if the `IFormMetadata` contains only a single view and the CantSave property is set to 'true'.
     */
    cantSave(formMetadata: IFormMetadata): boolean
    {
        return formMetadata.Views.length === formMetadata.Views.filter(x => x.CantSave).length;
    }

    /**
     * Returns `true` if the `IFormMetadata` contains only a single `IGridViewMetadata` without any editable functionality; `false` otherwise.
     *
     * @see MetadataViewService.IsReadOnlyGrid
     */
    isAdminForm(formStack: MetadataFormStack<AugmentedModelObject, RepositoryDataCollection>): boolean
    {
        if (Utilities.IsNullOrUndefined(formStack.form.IsAdminForm))
            return (
                this.isInitialForm(formStack) &&
                formStack.form.Views.length === 1 &&
                this.$metadataViewService.IsReadOnlyGrid(formStack.form.Views[0])
            );
        else return formStack.form.IsAdminForm;
    }

    /** Returns `true` if the `MetadataFormStack` has only its initial state; `false` otherwise. */
    isInitialForm(formStack: MetadataFormStack<AugmentedModelObject, RepositoryDataCollection>): boolean
    {
        return formStack.length === 1;
    }

    /**
     * Returns `true` if the `IFormMetadata` of the `MetadataFormStack`'s initial state was a `IStepperMetadata`; `false` otherwise.
     * @see MetadataViewService.IsStepper
     */
    isInitialFormStepper(formStack: MetadataFormStack<AugmentedModelObject, RepositoryDataCollection>): boolean
    {
        return this.$metadataViewService.IsStepper(formStack.initialState.formMetadata);
    }

    HasCustomSaveActions(value: any): value is IFormMetadataCustomSaveActions
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<IFormMetadataCustomSaveActions>("OverrideSaveActions", value)
        );
    }

    hasCustomCancelAction(value: any): value is IFormMetadataCustomCancelAction
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<IFormMetadataCustomCancelAction>("CancelAction", value)
        );
    }

    getCustomCancelLabel(value: any): string
    {
        let action = value["CancelAction"] as IMetadataAction;
        return !Utilities.IsNullOrUndefinedOrEmpty(action.Label) ? action.Label : "";
    }

    HasFooterView(value: IFormMetadata): boolean
    {
        return !Utilities.IsNullOrUndefined(value.FooterView);
    }

    /**
     * Sends the `FormState` that is the head of the `MetadataFormStack` to the standard `$repositoryService.VerifyAll` method.
     * If verification succeeds, then the promise will resolve normally. If verification fails, then the promise will be
     * rejected, and the verification errors will be sent to the `$metadataErrorService`.
     *
     * @param formStack `MetadataFormStack` to verify.
     */
    verify(
        formStack: MetadataFormStack<AugmentedModelObject, RepositoryDataCollection>,
        allowVerificationLeave: boolean = false
    ): Promise<AfterMetadataFormSave>
    {
        // Todo - Can this be a standardized MetadataAction?
        // const action = <IMetadataAction>{
        // 	ReturnPath: "RequireValid"
        // };
        // return this.$metadataActionService.ExecuteAction(undefined, action, formStack.dataCollection);
        const self = this;
        const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
            formStack.dataCollection,
            () => self.verify(formStack, allowVerificationLeave),
            formStack.model
        );
        let tempDataCollection = formStack.dataCollection;
        if (formStack.current.formMetadata.VerifyWithParent)
        {
            tempDataCollection = Utilities.deepCopy(formStack.dataCollection);
            let parentReference = formStack.model["$$Parent"];
            if (Utilities.IsNullOrUndefined(parentReference))
            {
                parentReference = formStack.previousState.model;
            }
            const parentCopy = Utilities.deepCopy(parentReference);
            let parentType = parentCopy["$$TypeName"];
            if (Utilities.IsNullOrUndefined(parentType))
            {
                parentType = formStack.previousState.formMetadata.BoundType;
            }
            tempDataCollection.OptionalParentEntity = parentCopy;
            tempDataCollection.OptionalParentType = parentType;
        }

        return this.$repositoryService.VerifyAll(tempDataCollection)
            .then(processResults, rejectionHandler);
        function processResults(verifyResults: MetadataCollectionVerification): Promise<void | AfterMetadataFormSave>
        {
            if (verifyResults.IsValid)
            {
                return Promise.resolve<AfterMetadataFormSave>({});
            }
            self.$metadataErrorService.setErrors(verifyResults, formStack.dataCollection);
            const title = "Verification Errors";
            self.$metadataErrorService.setErrors(verifyResults, formStack.dataCollection);
            const errors = self.$metadataErrorService.getErrorList(verifyResults);
            const message = errors.reduce((p, x) => `${p}<li>${x}</li>`, "");
            if (allowVerificationLeave)
            {
                return self.$commonDialogsService.showConfirmationMessage(message, title, "Back", "Stay and Fix")
                    .then(
                        _x => Promise.resolve<AfterMetadataFormSave>({ cancel: true }),
                        _r => Promise.reject());
            }
            else
            {
                return self.$commonDialogsService.showAlertMessage(message, title)
                    .then(
                        _x => Promise.reject(),
                        _r =>
                        {
                            /* Squashing errors */
                        });
            }
        }
    }

    /**
     * Registers a pair of functions that will intercept `metadata-form`'s save behavior and allow for custom handling.
     *
     * @param predicate Predicate `function` that will determine if the saveHandler is responsible for the `IFormMetadata` given as it's parameter.
     * @param handler `function` that will process a `IFormMetadata` and `RepositoryDataCollection` pair.
     */
    registerSaveHandler(predicate: MetadataFormSavePredicate, handler: MetadataFormSaveHandler): void
    {
        this.saveHandlers.push({
            predicate,
            handler,
        });
    }

    /**
     * If any save handlers have been registered with this service whose predicate function resolved to `true`,
     * then the save handler will be responsible for saving the  `IFormMetadata` and `RepositoryDataCollection` pair.
     *
     * If there are no registered save handlers, or none whose predicate resolve to `true`, then the default form save will occur.
     *
     * Save the `RepositoryDataCollection` of the `FormState` that is the head of the `MetadataFormStack`.
     * If the save, results in a list of 'Document'-like objects, then the `$fileService` will attempt to open each,
     * otherwise the current `FormState`'s dataController will be replaced with the results.
     *
     * If an `MetadataVerificationException` error occurs during the save process, `save()` will hand of the verification
     * to the `$metadataErrorService`, and display the verification results in a dialog.
     *
     * If any other error occurs, then the error message will be displayed in a dialog.
     *
     * @param formStack `MetadataFormStack` to execute a standard save with.
     */
    save(
        formStack: MetadataFormStack<AugmentedModelObject, RepositoryDataCollection>,
        overrideAction?: IMetadataAction
    ): Promise<AfterMetadataFormSave>
    {
        const self = this;
        const saveHandler = ArrayHelpers.first(this.saveHandlers, x => x.predicate(formStack.form));
        if (saveHandler)
        {
            return saveHandler.handler(formStack.form, formStack.dataCollection);
        }
        const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
            formStack.dataCollection,
            () => self.save(formStack, overrideAction),
            formStack.model
        );
        //If you use this chain you have to return a RepositoryDataCollection in the last action if it's not an intent action
        if (!Utilities.IsNullOrUndefined(overrideAction))
        {
            this.$intentService.delayIntentResolve = true;
            this.$metadataActionService.isOverrideSaveAction = true;
            return this.$metadataActionService
                .ExecuteAction(undefined, overrideAction, formStack.dataCollection)
                .then(processOverrideActionSaveResults, rejectionHandler)
                .finally(() => self.$metadataActionService.isOverrideSaveAction = false);
        }
        // Todo - Can this be a standardized MetadataAction?
        // const saveAction = <IMetadataAction>{
        //     ServicePath: undefined
        // };
        // return this.$metadataActionService.ExecuteAction(undefined, saveAction, formStack.dataCollection);
        return this.$repositoryService.SaveAll(formStack.dataCollection)
            .then(processSaveResults, rejectionHandler);
        function processOverrideActionSaveResults(
            saveResults: RepositoryDataCollection | AfterSaveContext
        ): Promise<void | AfterMetadataFormSave>
        {
            if (saveResults && saveResults["canceledActionDialog"] === true)
                return Promise.resolve({ halt: true });
            if (ObjectHelpers.isAfterSaveContext(saveResults))
                return processSaveResults({ AfterSaveContext: saveResults } as RepositoryDataCollection);
            return processSaveResults(saveResults as RepositoryDataCollection);
        }
        function processSaveResults(saveResults: RepositoryDataCollection): Promise<void | AfterMetadataFormSave>
        {
            formStack.dataCollection.ConfirmationQuestions = {};
            self.$metadataInputSelectService.ClearAllOptions();
            if (saveResults)
            {
                if (saveResults.Entity)
                {
                    formStack.current.replaceDataController(RepositoryFormData.create(saveResults));
                    // update object with any changes that occurred on the BE (like concurrency update)
                    Utilities.merge(formStack.model, saveResults.Entity);
                }
                if (!Utilities.IsNullOrUndefinedOrEmpty(saveResults.AfterSaveContext?.DocumentsToDownload) && saveResults.AfterSaveContext.DocumentsToDownload.filter(x => !!x && self.$metadataActionService.openDocumentInNewTab(x.MimeType)).length > 0)
                    self.$metadataActionService.delayTabClose = true;

                if (!Utilities.IsNullOrUndefined(overrideAction) && MetadataActionHelpers.isIntentAction(saveResults.AfterSaveContext?.PostAction) && saveResults.AfterSaveContext["$NextActionChain"])
                    return saveResults.AfterSaveContext["$NextActionChain"].then(() => self.$intentService.delayIntentResolve = false);

                return self.$metadataActionService.ProcessAfterSave(saveResults.AfterSaveContext, formStack.model, formStack.dataCollection)
                    .then(
                        () =>
                        {
                            if (
                                saveResults["halt"] === true ||
                                (saveResults.AfterSaveContext && saveResults.AfterSaveContext.Halt)
                            )
                                return Promise.resolve<AfterMetadataFormSave>({ halt: true });
                            return Promise.resolve<AfterMetadataFormSave>({ halt: false });
                        },
                        _r =>
                        {
                            /* Squashing errors */
                        }
                    );
            }
            return Promise.resolve({ halt: false });
        }
    }

    /**
     * Pushes a new `FormState` onto the given `MetadataFormStack`.
     *
     * @param formStack `MetadataFormStack` to push new state onto.
     * @param formMetadata `IFormMetadata` for new state.
     * @param model Model object for new state
     * @param dataController `RepositoryDataCollection` for new state
     * @param selectedView `IViewMetadata` to be selected in new state.
     */
    pushNewState(
        formStack: MetadataFormStack<AugmentedModelObject, RepositoryDataCollection>,
        formMetadata: IFormMetadata,
        model: AugmentedModelObject,
        dataController: RepositoryDataCollection,
        addMode: boolean,
        parentIsDirty: boolean,
        selectedView?: IViewMetadata
    ): void
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(formMetadata.Path))
        {
            formMetadata.Path = formStack.form.Path;
        }
        formStack.goTo(
            formMetadata,
            model,
            this.$formTitleService.getTitle(),
            dataController,
            addMode,
            parentIsDirty,
            selectedView || formMetadata.Views[0]
        );
    }

    /**
     * Pops the current state off the `formStack` and resolves the associated data based on the `cancelled` flag.
     *
     * If `cancelled` is `true`, then
     *
     * @param formStack `MetadataFormStack` to operate on.
     * @param cancelled Flag to determine how the popped formState's data will be handled
     */
    navigateBack(
        formStack: MetadataFormStack<AugmentedModelObject, RepositoryFormData>,
        cancelled: boolean
    ): FormState<any, any>
    {
        const self = this;
        // WARNING - stack.pop() affects this.currentState, this.currentForm, this.currentModel, and this.currentDataController!
        const leavingState = formStack.pop();
        this.$providerStorageService.ClearGridProviders(leavingState.formMetadata);

        formStack.dataCollection["$$childFormIsEditing"] = false;
        this.$multipleEditEntityService.IsMultipleEditing = false;
        this.$metadataEventService.fireLookupTypeCacheClearListener("DocumentTypeName");
        this.$metadataEventService.fireLookupTypeCacheClearListener("UserDefinedData");
        this.$formTitleService.setTitle(leavingState.previousTitle);

        const isAdminForm = this.isAdminForm(formStack);
        const viewToUpdate = this.getViewToUpdate(formStack.current.selectedView, leavingState);
        // Remove the primary entity if it was Added and not persisted on the backend
        if (
            leavingState.model.SerializedRowState &&
            leavingState.model.SerializedRowState === "Added" &&
            cancelled &&
            leavingState.addMode
        )
        {
            const filter = self.$metadataViewService.createFilterString(
                leavingState.model,
                viewToUpdate
            );
            leavingState.datacontroller.Remove(leavingState.formMetadata.BoundType, leavingState.model, filter);
            // Todo - Should we only have to do this if `leavingState.dataController === formStack.current.dataController`?
            // Todo - What about OtherEntities that may have been added?
        }

        this.syncOtherEntities(formStack, leavingState, cancelled);

        if (leavingState.formMetadata.RecalculateParentFormOnSave && !cancelled)
            this.$metadataLoadViewService.recalculating[leavingState.formMetadata.BoundType] = true;

        if (this.$metadataViewService.IsProjectionGrid(viewToUpdate))
        {
            if (cancelled)
            {
                if (this.isInitialForm(formStack))
                {
                    this.$cacheHelperService.clearAllForType(leavingState.datacontroller.Type);
                    formStack.current.datacontroller.Remove(leavingState.formMetadata.BoundType, leavingState.model, null);
                }
            }
            else
            {
                if (leavingState.datacontroller.Entity.SerializedRowState === null && leavingState.addMode)
                    leavingState.datacontroller.Entity.SerializedRowState = "Added";
                viewToUpdate["$projectionGridUpdatingEntity"] = leavingState.datacontroller.Entity;
            }
        }

        const leavingSelectedView = leavingState.formMetadata.Views.filter(
            v => v.Title === leavingState.selectedView.Title
        )[0];
        const recalculateOnCancel = cancelled && this.isInitialForm(formStack) && this.cantSave(formStack.form) && leavingState.model.SerializedRowState !== "Added";
        // for admin type views need to reload when we navigate back (or if specified)
        if (this.$metadataViewService.IsProjectionGrid(viewToUpdate))
        {
            this.$repositoryService.ClearAll();
        }
        else if (
            (Utilities.IsNullOrUndefined(leavingSelectedView.ReloadOnSave) || leavingSelectedView.ReloadOnSave) &&
            ((isAdminForm && !this.$metadataViewService.IsEditFormSaveDelayed(formStack.form.Views[0])) ||
                (!cancelled && leavingSelectedView.ReloadOnSave === true))
        )
        {
            window.location.reload();
            this.$repositoryService.ClearAll();
        }
        else if (recalculateOnCancel)
        {
            this.$repositoryService.ClearFormCache(leavingState.datacontroller);
        }

        if (recalculateOnCancel && leavingState.formMetadata.RecalculateParentFormOnSave)
        {
            this.$metadataLoadViewService.recalculating[leavingState.formMetadata.BoundType] = true;
        }

        // TODO - review! SetupNewForm fires this.formNavigate, do we need to actually do that here???
        // if (!Utilities.IsNullOrUndefined(this.formNavigate))
        // {
        //     this.formNavigate({ form: this.currentForm });
        // }
        if (
            leavingState.model.SerializedRowState &&
            leavingState.model.SerializedRowState === "Added" &&
            cancelled &&
            leavingState.addMode
        )
        {
            this.handleCancelAdded(leavingState.model, formStack.dataCollection, formStack.model, viewToUpdate);
        }
        return leavingState;
    }

    syncOtherEntities(formStack: MetadataFormStack<AugmentedModelObject, RepositoryFormData>, leavingState: FormState<AugmentedModelObject, RepositoryFormData>, cancelled: boolean): void
    {
        const isAdminForm = this.isAdminForm(formStack);
        if (!(isAdminForm || this.cantSave(formStack.current.formMetadata)) && !cancelled && leavingState.formMetadata.SaveHandling !== SaveHandlingType.Immediate)
        {
            // We need to add any entities that are in the leavingState's dataController to the currentDataController.
            // TODO - should we check to see if they are already in our currentDataController? or be aware of their SerializedRowState?
            for (const typeName in leavingState.datacontroller.OtherEntities)
            {
                const entities = leavingState.datacontroller.OtherEntities[typeName];
                for (const entity of entities)
                {
                    // REVIEW do we need to keep track of the entities create filter?
                    formStack.dataCollection.Add(typeName, entity, null);
                }
            }
            formStack.dataCollection.SyncOtherEntityCache(leavingState.datacontroller);
        }
    }

    getViewToUpdate(view: IViewMetadata | ICombinedViewMetadata, leavingState: FormState<AugmentedModelObject, RepositoryFormData>): IViewMetadata
    {
        const self = this;
        let ret: IViewMetadata = null;
        if (this.$metadataViewService.IsTabbedView(view))
        {
            if (view.Views.some(v => v["$currentIndex"]))
                ret = this.getViewToUpdate(view.Views.find(v => v["$currentIndex"]), leavingState);
            else
            {
                this.getViewToUpdate(view.Views.find(v => this.$metadataViewService.InitialViewOfTabbedView(v)), leavingState);
            }
        }
        else if (this.$metadataViewService.IsCombinedView(view))
        {
            (<ICombinedViewMetadata>view).Views.forEach(v =>
            {
                if (ret != null) return;
                if (this.$metadataViewService.IsCombinedView(v))
                {
                    const child = this.getViewToUpdate(v, leavingState);
                    if (child != null) ret = child;
                }
                else if (isViewToUpdate(v)) ret = v;
            });
        }
        if (Utilities.IsNullOrUndefined(ret))
            ret = view;

        function isViewToUpdate(view: IViewMetadata): boolean
        {
            if (self.$metadataViewService.IsProjectionGrid(view))
                return view.GridProperties["RowTypeName"] === leavingState.datacontroller.Type;
            else if (self.$metadataViewService.IsGrid(view))
                return view.GridProperties.ClassMetadata.TypeName === leavingState.datacontroller.Type;
            return false;
        }

        return ret;
    }

    handleCancelAdded(leavingModel: any, datacontroller: RepositoryFormData, model: any, view: IViewMetadata): void
    {
        const self = this;
        const subProp = view.SubPropertyName;
        if (
            !Utilities.IsNullOrUndefined(subProp) &&
            (!leavingModel.SerializedRowState || leavingModel.SerializedRowState === "Added")
        )
        {
            const subPropertyModel = getEntityFromSubProperty(model, subProp);
            if (subPropertyModel && !view.SubPropertyType)
            {
                //subProperty of the main entity
                removeEntityFromSubproperty(subPropertyModel, leavingModel, view);
            }
            if (view.SubPropertyType && datacontroller.OtherEntities[view.SubPropertyType])
            {
                //subpropertytype is set, so check otherEntities' subproperty
                const entity = datacontroller.OtherEntities[view.SubPropertyType][0];
                if (entity && entity[subProp])
                {
                    removeEntityFromSubproperty(entity[subProp], leavingModel, view);
                }
            }
        }
        else if (this.$metadataViewService.IsGrid(view))
        {
            const filter = self.$metadataViewService.createFilterString(model, view);
            datacontroller.Remove(view.GridProperties.ClassMetadata.TypeName, leavingModel, filter);
        }

        function removeEntityFromSubproperty(subPropertyModel: any[], entity: unknown, view: IViewMetadata): void
        {
            const index = subPropertyModel.indexOf(entity);
            if (index >= 0)
            {
                subPropertyModel.splice(index, 1);
                return;
            }
            //Handle Projection grids or any other case where the model reference isn't shared for some reason between
            // the old form state and the grid entities

            if (self.$metadataViewService.IsGrid(view) && view.GridProperties.AddToTop)
            {
                if (subPropertyModel[0].SerializedRowState === "Added")
                    subPropertyModel.shift();
            }
            else
            {
                if (subPropertyModel[subPropertyModel.length - 1].SerializedRowState === "Added")
                    subPropertyModel.pop();
            }
        }
        function getEntityFromSubProperty(entity: any, SubPropertyName: string): any[]
        {
            const chain = SubPropertyName.split(".");
            let subPropertyEntity = Object.assign({}, entity);
            for (let i = 0; i < chain.length; i++)
            {
                if (!subPropertyEntity) return subPropertyEntity;
                const element = chain[i];
                subPropertyEntity = subPropertyEntity[element];
            }
            return subPropertyEntity;
        }
    }
}
